import { IconFont } from '@/components';
import { RoundedTab } from '@/components/RoundedTab';
import Tabs from '@/components/Tabs';
import { useSafeCallback } from '@/hooks';
import { Button, Form, Grid, Input, InputNumber, Select } from '@arco-design/web-react';
import { IconDelete, IconEdit } from '@arco-design/web-react/icon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  LifecycleManageData,
  LifecycleManageTab,
  useLifecycleManageStore,
} from './useLifecycleManageStore';

const TabPane = Tabs.TabPane;
const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;
const TextArea = Input.TextArea;

type LifecycleManageTabMap = Record<LifecycleManageTab, LifecycleManageTab>;

const tabMap: LifecycleManageTabMap = {
  HTTP: 'HTTP',
  COMMAND: 'COMMAND',
  TCP_PORT: 'TCP_PORT',
};

const getMsg = (t: any) => ({
  http: `HTTP ${t('components:request')}`,
  type: t('common:type'),
  command: t('components:command'),
  tcp_port: `TCP ${t('common:port')}`,
  path: t('common:path'),
  command_help: t('components:use.half.width.commas.to.separate.multiple.commands'),
  port: t('common:port'),
  port_error: t('components:please.enter.port'),
});

interface LifecycleManagePannelsProps {
  /** 编辑初始化的时候挂载的已有/默认数据 */
  data?: LifecycleManageData;
  /** 变化时回调: 外部点击确认才保存的情况 - 通过 onChange 收集数据,点击确认才赋值给对应的状态 */
  onChange?: (data: LifecycleManageData) => void;
}

const initialValues: LifecycleManageData = {
  tab: tabMap.HTTP,
  // HTTP请求默认值
  protocol: 'HTTP',
  path: '/',
  httpPort: 80,
  // 执行的命令默认值
  command: '',
  // TCP端口默认值
  tcpPort: 80,
};

export function LifecycleManagePannels({
  data,
  onChange: propsOnChange,
}: LifecycleManagePannelsProps) {
  const { t } = useTranslation();
  const msg = getMsg(t);
  const [form] = Form.useForm<LifecycleManageData>();
  const [tab, setTab] = useState<LifecycleManageTab>(tabMap.HTTP);
  const [allValues, setAllValues] = useState<LifecycleManageData>(initialValues);
  const onChange = useSafeCallback(propsOnChange);
  const setValidateLifecycleManage = useLifecycleManageStore(
    (state) => state.setValidateLifecycleManage,
  );

  const handleTabChange = async (key: LifecycleManageTab) => {
    // 1. 获取当前表单值
    const currentValues = form.getFieldsValue();
    // 2. 更新 allValues 状态，合并当前表单值
    setAllValues((prevValues) => ({ ...prevValues, ...currentValues, tab: key }));
    // 3. 更新 tab
    setTab(key);
    // 4. 触发 onChange，传递更新后的 allValues
    onChange?.({ ...allValues, ...currentValues, tab: key });
  };

  const validateFields = useCallback(async () => {
    switch (tab) {
      case 'COMMAND':
        try {
          await form.validate(['command']);
          return true;
        } catch (error) {
          return false;
        }
      case 'TCP_PORT':
        try {
          await form.validate(['tcpPort']);
          return true;
        } catch (error) {
          return false;
        }
      default:
        return Promise.resolve(true);
    }
  }, [form, tab]);

  // 共享校验函数
  useEffect(() => {
    setValidateLifecycleManage(validateFields);
  }, [validateFields, setValidateLifecycleManage]);

  // data 有值时，初始化表单
  useEffect(() => {
    if (data) {
      form.setFieldsValue(data);
      onChange(data); // 这是触发收集数据更新为已有的的数据
      setAllValues(data); // 初始化 allValues
      setTab(data.tab || tabMap.HTTP);
    } else {
      // 新增时初始化表单
      onChange(initialValues);
      setAllValues(initialValues); // 初始化 allValues
    }
  }, [data, onChange, form]);

  return (
    <Container>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={(_, values) => {
          onChange({ ...values, tab });
        }}
      >
        <RoundedTab activeTab={tab} onChange={handleTabChange}>
          <TabPane key={tabMap.HTTP} title={msg.http}>
            <Row gutter={8}>
              <Col span={8}>
                <FormItem label={msg.path} required field="protocol">
                  <Select
                    showSearch
                    options={[
                      { label: 'HTTP', value: 'HTTP' },
                      { label: 'HTTPS', value: 'HTTPS' },
                    ]}
                  />
                </FormItem>
              </Col>
              <Col span={8}>
                <FormItem label={<>&nbsp;</>} field="path">
                  <Input />
                </FormItem>
              </Col>
              <Col span={8}>
                <FormItem label={<>&nbsp;</>} field="httpPort">
                  <InputNumber />
                </FormItem>
              </Col>
            </Row>
          </TabPane>
          <TabPane key={tabMap.COMMAND} title={msg.command}>
            <FormItem
              layout="vertical"
              label={msg.command}
              required
              help={msg.command_help}
              field="command"
              rules={[{ required: true }]}
            >
              <TextArea autoSize={{ minRows: 2 }} />
            </FormItem>
          </TabPane>
          <TabPane key={tabMap.TCP_PORT} title={msg.tcp_port}>
            <FormItem
              layout="vertical"
              label={msg.port}
              required
              field="tcpPort"
              rules={[{ required: true, message: msg.port_error }]}
            >
              <InputNumber />
            </FormItem>
          </TabPane>
        </RoundedTab>
        <FormItem />
      </Form>
    </Container>
  );
}

/**
 * 配置好了的探针展示组件
 */
export function LifecycleItem({
  data,
  onEditClick,
  onDeleteClick,
}: {
  data?: LifecycleManageData;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
}) {
  const { t } = useTranslation();
  const msg = getMsg(t);

  const texts = useMemo(() => {
    switch (data?.tab) {
      case 'TCP_PORT':
        return { title: msg.tcp_port };
      case 'COMMAND':
        return { title: msg.command };
      case 'HTTP':
      default:
        return {
          title: msg.http,
        };
    }
  }, [data, msg]);

  const matchTab = useMemo(() => {
    switch (data?.tab) {
      case 'TCP_PORT':
        return (
          <>
            <InfoItemBox>
              <span>{data?.tcpPort}</span>
              <p>{msg.port}</p>
            </InfoItemBox>
          </>
        );
      case 'COMMAND':
        return (
          <>
            <InfoItemBox>
              <Command>{data?.command}</Command>
              <p>{msg.command}</p>
            </InfoItemBox>
          </>
        );
      case 'HTTP':
      default:
        return (
          <>
            <InfoItemBox>
              <Command>{data?.protocol}</Command>
              <p>{msg.type}</p>
            </InfoItemBox>
            <InfoItemBox>
              <Command>{data?.path}</Command>
              <p>{msg.path}</p>
            </InfoItemBox>
            <InfoItemBox>
              <Command>{data?.httpPort}</Command>
              <p>{msg.port}</p>
            </InfoItemBox>
          </>
        );
    }
  }, [data, msg]);

  return (
    <DisplayBox>
      <InfoBox>
        <IconFont name="icon-jiankangjiancha" style={{ fontSize: 36 }} />
        <InfoTitleBox>
          <span>{texts.title}</span>
        </InfoTitleBox>
        {matchTab}
      </InfoBox>
      <ActionBox>
        <Button type="text" icon={<IconEdit />} style={{ color: 'gray' }} onClick={onEditClick} />
        <Button type="text" status="danger" icon={<IconDelete />} onClick={onDeleteClick} />
      </ActionBox>
    </DisplayBox>
  );
}

/** ============================ 样式 ==================================== */
const Container = styled.div`
  padding: 16px;
`;

// 展示数组的组件
const DisplayBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const InfoBox = styled.div`
  display: flex;
  gap: 16px;
  i.iconfont {
    opacity: 0.45;
  }
`;
const InfoLayout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #000;
  /** 标题 */
  span {
  }
  /** 描述 */
  p {
    font-size: 12px;
    opacity: 0.5;
  }
`;
const InfoTitleBox = styled(InfoLayout)`
  min-width: 100px;
`;
const InfoItemBox = styled(InfoLayout)`
  width: 100px;
  margin-left: 20px;
`;
const Command = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionBox = styled.div`
  display: flex;
  gap: 8px;
`;
